import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Business from './components/Business.js';
import PrivateEquity from './components/PrivateEquity.js';
import TechnologyRoute from './components/technologies/TechnologyRoute';
import Technologies from './components/Technologies.js';
import OurCompany from './components/OurCompany.js';
import ServiceRoute from './components/services/ServiceRoute';
import AIServiceRoute from './components/ai/AIServiceRoute';
import Navbar from './components/Navbar.js';
import ProtectedRoute from './components/ProtectedRoute.js';
import EmployeePortal from './components/EmployeePortal.js';
import BusinessList from './components/EmployeeBusinessList.js';
import BlogPost from './components/EmployeeBlog.js';
import Home from './components/Home.js';
import Contact from './components/frontend/ContactForm';
import BottomNav from './components/BottomNav.js';
import { Functionality } from './context/Functionality.js';
import Login from './components/auth/login.js';
import Register from './components/auth/register.js';
import EmailVerification from './components/auth/EmailVerification';
import Referrals from './components/Referrals';
import ClientForms from './components/ClientForms';
import PublicAssessmentForm from './components/PublicAssessmentForm';
import AccountPortal from './components/AccountPortal';
import AccountPortals from './components/AccountPortals';
import PasswordReset from './components/auth/PasswordReset.js';
import AutomationTokens from './components/AutomationTokens';
import AddBlog from './components/AddBlog.js';
import EmployeePost from './components/EmployeePost.js';
import { PublicArticle, LegacyArticle } from './components/blog/PublicBlog';
import NotFound from './components/NotFound'; // Create this component for 404
import Services from './components/Services.js';
import AssessmentCalculator from './components/calculators/AssessmentCalculator';

import PageMetadata from './components/seo/MetaHead';
import { pageMetadata } from './components/seo/pageMetadata';

const publicPage = (path, content) => <PageMetadata meta={pageMetadata(path)}>{content}</PageMetadata>;

function App() {
  const { token } = useContext(Functionality);
  const location = useLocation();

  // Check if the current path is either login or register
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].includes(location.pathname);
  const innerPages = ['/client-forms','/portal', '/account-portals', '/referrals', '/automation-tokens', '/blog-post', '/business-list', '/add-blog', '/calculators/business', '/calculators/private-equity'];
  const isFormPage = location.pathname.startsWith('/forms/');
  const isInnerPage = isFormPage || location.pathname.startsWith('/portal/') || innerPages.includes(location.pathname) || location.pathname.startsWith('/add-blog/') || location.pathname.startsWith('/blog-preview/');

  useEffect(() => {
    const privatePage = isAuthPage || isInnerPage || location.pathname === '/employeePortal';
    let tag = document.head.querySelector('meta[name="robots"][data-employee]');
    if (privatePage && !tag) {
      tag = document.createElement('meta'); tag.name = 'robots'; tag.dataset.employee = 'true'; document.head.appendChild(tag);
    }
    if (privatePage) tag.content = 'noindex, nofollow';
    else tag?.remove();
    return () => tag?.remove();
  }, [isAuthPage, isInnerPage, location.pathname]);

  return (
    <div className="app-wrapper">
      {!isFormPage && <Navbar />}
      {/* {token && <EmployeePortal />} */}
      <Routes>
        <Route path="/forms/invitation" element={<PublicAssessmentForm key="email-invitation" />} />
        <Route path="/forms/:invitation" element={<PublicAssessmentForm key={location.pathname} />} />
        <Route path="/client-forms" element={<ProtectedRoute><EmployeePortal><ClientForms /></EmployeePortal></ProtectedRoute>} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/portal/:id?" element={<ProtectedRoute employeeOnly={false}><AccountPortal /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><EmployeePortal><Referrals employeeView /></EmployeePortal></ProtectedRoute>} />
        <Route path="/account-portals" element={<ProtectedRoute><EmployeePortal><AccountPortals /></EmployeePortal></ProtectedRoute>} />
        <Route
          path="/employeePortal"
          element={
            <ProtectedRoute token={token} redirectTo="/">
              <EmployeePortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business-list"
          element={
            <ProtectedRoute token={token} redirectTo="/">
              <EmployeePortal>
                <BusinessList />
              </EmployeePortal>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog-post"
          element={
            <ProtectedRoute token={token} redirectTo="/">
              <EmployeePortal>
                <BlogPost />
              </EmployeePortal>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-blog/:blogId?"
          element={
            <ProtectedRoute token={token} redirectTo="/">
              <EmployeePortal>
                <AddBlog key={location.pathname} />
              </EmployeePortal>
            </ProtectedRoute>
          }
        />
        <Route path="/automation-tokens" element={<ProtectedRoute token={token} redirectTo="/login"><EmployeePortal><AutomationTokens /></EmployeePortal></ProtectedRoute>} />
        <Route path="/calculators/business" element={<ProtectedRoute token={token} redirectTo="/login"><EmployeePortal><AssessmentCalculator key="business" /></EmployeePortal></ProtectedRoute>} />
        <Route path="/calculators/private-equity" element={<ProtectedRoute token={token} redirectTo="/login"><EmployeePortal><AssessmentCalculator key="private-equity" portfolio /></EmployeePortal></ProtectedRoute>} />
        <Route path="/" element={publicPage('/', <Home />)} />
        <Route path="/blog-preview/:blogId" element={<ProtectedRoute token={token} redirectTo="/login"><EmployeePortal><EmployeePost /></EmployeePortal></ProtectedRoute>} />
        <Route path="/blog" element={<Navigate to="/" replace />} />
        <Route path="/blog/:slug" element={<PublicArticle />} />
        <Route path="/employee-post/:blogId" element={<LegacyArticle />} />
        <Route path="/contact" element={publicPage('/contact', <Contact />)} />
        <Route path="/clients" element={publicPage('/clients', <Business />)} />
        <Route path="/private-equity" element={publicPage('/private-equity', <PrivateEquity />)} />
        <Route path="/services" element={<ServiceRoute />} />
        <Route path="/services/:slug" element={<ServiceRoute />} />
        <Route path="/ios" element={<TechnologyRoute ios />} />
        <Route path="/technologies/:slug" element={<TechnologyRoute />} />
        <Route path="/technologies" element={publicPage('/technologies', <Technologies />)} />
        <Route path="/technologies/artificial-intelligence" element={<AIServiceRoute />} />
        <Route path="/technologies/artificial-intelligence/:slug" element={<AIServiceRoute />} />
        <Route path="/our-company" element={publicPage('/our-company', <OurCompany />)} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<PasswordReset key="forgot" />} />
        <Route path="/reset-password" element={<PasswordReset key="reset" reset />} />
        <Route path="/register" element={<Register />} />
        <Route path="/service/:id" element={<Services />} />
        {/* Catch-all route for 404 - Page Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAuthPage && !isInnerPage && <BottomNav />}
    </div>
  );
}

export default App;
